import React from "react";
import styled from "styled-components";
import fetch from "./shared/Fetch";
import { getCurrentBudgetYear } from "./shared/TimeUtil";
import Spinner from "./sharedComponents/Spinner";
import AppHeading from "./sharedComponents/AppHeading";
import { AddNewButton } from "./sharedComponents/Atoms";
import { ReactComponent as CloseIcon } from "./svgs/CloseIcon.svg";
import { ReactComponent as AddIcon } from "./svgs/AddIcon.svg";

const truncate = (str = "", max = 18) => {
  if (typeof str !== "string") return "";
  return str.length > max ? str.slice(0, max).trimEnd() + "…" : str;
};

const ReportContainer = styled.div`
  padding: 5rem 10rem;
`;

const BudgetTable = styled.table`
  margin-top: 5rem;

  td {
    position: relative;
  }
  td:hover {
    outline: 1px solid #000;
    outline-offset: -1px;
    z-index: 1;
  }
`;

const StyledBudgetRow = styled.tr`
  &:nth-child(2n) {
    background: var(--table-band-background);
  }
  &:hover {
    background: var(--table-hover-background);
  }
`;

const StyledBudgetData = styled.td`
  white-space: nowrap;
  text-align: center;
  padding: 0.5rem;
`;

const BudgetDataInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 1rem;
  background: transparent;
  border: 0;
`;

const DepartmentsContainer = styled.div`
  display: grid;
  grid-template-rows: max-content max-content;
  gap: 1.5rem;
`;

const SectionTitle = styled.span`
  font-size: 1.2rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const DepartmentButton = styled.button`
  background: ${props =>
    props.selected
      ? "var(--filter-button-selected)"
      : "var(--button-background-color)"};
  border-radius: 5rem;
  color: ${props =>
    props.selected ? "white" : "var(--filter-button-selected)"};
  padding: 0.5rem 0.7rem;
  border: 1px solid
    ${props =>
      props.selected ? "transparent" : "var(--filter-button-selected)"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: none;
  font-weight: ${props => (props.selected ? "bold" : "normal")};

  &:focus {
    outline: none;
  }

  &:hover {
    color: ${props =>
      props.selected ? "white" : "var(--filter-button-hovered-color)"};
    border-color: ${props =>
      props.selected ? "transparent" : "var(--filter-button-hovered-color)"};
  }

  & svg:first-child {
    fill: red;
    margin-right: 0.5rem;
    display: ${props => (props.selected ? "grid" : "none")};
  }
`;

const AddIconStyled = styled(AddIcon)`
  margin-right: 0.5rem;
  height: 1.3rem;
  width: 1.3rem;
  & * {
    fill: var(--button-primary-color);
  }
`;

class Budget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      budget: [],
      group: "",
      groups: []
    };
    this.addNewBudgetYear = this.addNewBudgetYear.bind(this);
  }

  async componentDidMount() {
    this.fetchGroups();
  }

  render() {
    const { budget, group, groups } = this.state;
    return (
      <div className="module">
        <AppHeading
          departments={this.props.match.params.departments}
          offset={this.props.match.params.offset}
          weeks={this.props.match.params.weeks}
          groups={this.props.match.params.groups}
          availableOnly={this.props.match.params.availableOnly}
          seniorities={this.props.match.params.seniorities}
          serviceareas={this.props.match.params.serviceareas}
          active={"budget"}
        />
        <ReportContainer>
          <DepartmentsContainer>
            <SectionTitle>Velg tjeneste</SectionTitle>
            <ButtonsContainer>
              {groups &&
                groups.map((d, i) => (
                  <DepartmentButton
                    key={d.id + "" + i}
                    onClick={() =>
                      this.setState({ budget: [], group: d.id }, () => {
                        this.fetchBudget();
                      })
                    }
                    selected={this.state.group === d.id}
                  >
                    <CloseIcon />
                    <span title={d.name}> {truncate(d.name, 18)}</span>
                  </DepartmentButton>
                ))}
            </ButtonsContainer>
          </DepartmentsContainer>

          <form>
            <Spinner show={this.state.waitingForData} />
            <BudgetTable>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>FY</th>
                  <th>Juli</th>
                  <th>August</th>
                  <th>September</th>
                  <th>Oktober</th>
                  <th>November</th>
                  <th>Desember</th>
                  <th>Januar</th>
                  <th>Februar</th>
                  <th>Mars</th>
                  <th>April</th>
                  <th>Mai</th>
                  <th>Juni</th>
                </tr>
              </thead>
              <tbody>
                {budget.map(b => {
                  return (
                    <BudgetRow
                      key={group + b.year}
                      budgetYear={b}
                      group={group}
                    />
                  );
                })}
              </tbody>
            </BudgetTable>

            <AddNewButton type="submit" onClick={this.addNewBudgetYear}>
              <AddIconStyled />
              Nytt budsjettår
            </AddNewButton>
          </form>
        </ReportContainer>
      </div>
    );
  }

  addNewBudgetYear(e) {
    e.preventDefault();

    const newYear =
      this.state.budget.length > 0
        ? this.state.budget[this.state.budget.length - 1].year // Juli i inneværende år, regnes som neste år.
        : getCurrentBudgetYear();
    fetch("/budget/", {
      method: "POST",
      body: JSON.stringify({
        groupId: this.state.group,
        yearMonth: newYear + "07",
        value: 0
      })
    }).then(
      () => {
        this.fetchBudget();
      },
      reason => console.log("Something went wrong during the request", reason)
    );
  }

  fetchBudget() {
    this.setState({ waitingForData: true });
    fetch("/budget?groups=" + this.state.group)
      .then(response => {
        this.setState({ budget: response || [], waitingForData: false });
      })
      .catch(reason => {
        console.log("Something went wrong during the request", reason);
        this.setState({ budget: [], waitingForData: false });
      });
  }

  fetchGroups() {
    this.setState({ waitingForData: true });

    fetch("/groups")
      .then(response => {
        const list = Array.isArray(response) ? response : [];
        const firstId = list.length > 0 ? list[0].id : "";
        this.setState(
          {
            groups: list,
            group: firstId
          },
          () => {
            if (firstId) {
              this.fetchBudget();
            } else {
              this.setState({ waitingForData: false });
            }
          }
        );
      })
      .catch(reason => {
        console.log("Something went wrong while fetching groups", reason);
        this.setState({ groups: [], group: "", waitingForData: false });
      });
  }
}

class BudgetRow extends React.Component {
  render() {
    const { budgetYear } = this.props;
    return (
      <StyledBudgetRow>
        <td style={{ fontWeight: "bold", fontSize: "0.7rem" }}>
          {budgetYear.year}
        </td>
        {budgetYear.months.map(month => (
          <BudgetData
            key={this.props.group + month.yearMonth}
            budgetMonth={month}
            group={this.props.group}
          />
        ))}
      </StyledBudgetRow>
    );
  }
}

class BudgetData extends React.Component {
  constructor(props) {
    super(props);
    this.saveBudgetEntry = this.saveBudgetEntry.bind(this);
  }

  render() {
    return (
      <StyledBudgetData style={{ textAlign: "center" }}>
        <BudgetDataInput
          defaultValue={this.props.budgetMonth.value}
          onBlur={this.saveBudgetEntry}
        />
      </StyledBudgetData>
    );
  }

  saveBudgetEntry(e) {
    if (e.currentTarget.value !== this.props.budgetMonth.value) {
      fetch("/budget/", {
        method: "POST",
        body: JSON.stringify({
          groupId: this.props.group,
          yearMonth: this.props.budgetMonth.yearMonth,
          value: e.currentTarget.value
        })
      }).catch(reason =>
        console.log("Something went wrong during the request", reason)
      );
    }
  }
}

export default Budget;
